import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Check, ChevronDown, Clock, Monitor, RefreshCw, Award, ArrowRight, Heart, Users, Shield, Repeat, BookOpen, ClipboardCheck, Zap, AlertTriangle, HeartPulse, Bandage, Thermometer, Skull, Brain, Bone, Stethoscope, Hand, Search } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { VideoCourseTrust } from '../components/VideoCourseTrust';
// --- Curriculum Data ---
const modules = [{
  title: 'Module 1: Foundations',
  lessons: ['Primary Pet Assessment (PPA)', 'Snout-to-Tail Wellness Check', 'Reading Vital Signs', 'When to Call the Emergency Vet'],
  icon: Stethoscope
}, {
  title: 'Module 2: CPR & Rescue Breathing',
  lessons: ['Recognizing Cardiac Arrest in Pets', 'Chest Compressions for Dogs', 'Chest Compressions for Cats', 'Rescue Breathing Technique', 'Full CPR Sequence & Timing'],
  icon: HeartPulse
}, {
  title: 'Module 3: Choking & Airway Emergencies',
  lessons: ['Signs of Choking', 'Modified Heimlich for Dogs', 'Airway Clearance for Cats', 'Post-Choking Assessment'],
  icon: AlertTriangle
}, {
  title: 'Module 4: Bleeding & Wound Management',
  lessons: ['Controlling Bleeding', 'Pressure Bandaging Techniques', 'Wound Cleaning & Infection Prevention', 'When Stitches Are Needed'],
  icon: Bandage
}, {
  title: 'Module 5: Environmental Emergencies',
  lessons: ['Heatstroke Recognition & Response', 'Hypothermia & Cold Injury', 'Common Household Poisons', 'Outdoor & Seasonal Toxins'],
  icon: Thermometer
}, {
  title: 'Module 6: Neurological & Trauma',
  lessons: ['Seizure Management', 'Fracture & Trauma Basics', 'Safe Emergency Transport', 'Spinal Injury Awareness'],
  icon: Brain
}, {
  title: 'Module 7: Handling & Restraint',
  lessons: ['Muzzling Basics for Safety', 'Restraint Techniques for Injured Pets', 'Moving an Immobile Animal'],
  icon: Hand
}];
// --- FAQ Data ---
const faqs = [{
  q: 'Do I need any prior experience?',
  a: 'No. The course is designed for complete beginners. We start with the fundamentals and build up to advanced emergency response skills.'
}, {
  q: 'Is this course enough without the in-person workshop?',
  a: 'The video course gives you comprehensive knowledge of every core pet emergency skill. For hands-on physical practice with mannequins and instructor feedback, you can add a workshop later. Many students start with the video course and upgrade when ready.'
}, {
  q: 'Can I upgrade to certification later?',
  a: 'Absolutely. Your video course purchase applies toward the full certification pathway. You can add the in-person skills assessment at any time to earn your 2-year Pet CPR & First Aid Certification.'
}, {
  q: 'How long is the course?',
  a: 'The course includes 25+ video lessons totaling approximately 6 hours of content. Most students complete it in 1–2 weeks watching a few lessons per day, but you can go at your own pace.'
}, {
  q: 'Is certification included with the video course?',
  a: 'You receive a Certificate of Completion after finishing all modules and passing the knowledge checks. The full 2-year professional certification requires an additional in-person skills assessment.'
}, {
  q: 'Can I rewatch lessons?',
  a: 'Yes. You have lifetime access to all course content. Rewatch any lesson as many times as you need.'
}];
// --- FAQ Accordion ---
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-warm-gray-200 py-5" data-id="element-731">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full justify-between items-center text-left group" aria-expanded={isOpen} data-id="element-732">
        <span className="font-heading text-xl text-primary-900 group-hover:text-accent-500 transition-colors pr-8" data-id="element-733">
          {question}
        </span>
        <ChevronDown className={`w-5 h-5 text-accent-500 transition-transform duration-300 shrink-0 ${isOpen ? 'rotate-180' : ''}`} data-id="element-734" />
      </button>
      <AnimatePresence data-id="element-735">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-736">
            <p className="pt-4 pb-2 text-warm-gray-700 font-body leading-relaxed" data-id="element-737">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// --- Curriculum Accordion ---
function ModuleItem({
  module,
  index
}: {
  module: (typeof modules)[0];
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(index === 0);
  const Icon = module.icon;
  return <div className="bg-white rounded-2xl shadow-warm border border-warm-gray-100 overflow-hidden" data-id="element-738">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full items-center gap-4 p-6 text-left group" aria-expanded={isOpen} data-id="element-739">
        <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-accent-50 transition-colors" data-id="element-740">
          <Icon className="w-5 h-5 text-primary-800 group-hover:text-accent-600" data-id="element-741" />
        </div>
        <div className="flex-grow" data-id="element-742">
          <h3 className="font-heading text-lg text-primary-900 group-hover:text-accent-600 transition-colors" data-id="element-743">
            {module.title}
          </h3>
          <p className="font-body text-sm text-warm-gray-500" data-id="element-744">
            {module.lessons.length} lessons
          </p>
        </div>
        <ChevronDown className={`w-5 h-5 text-accent-500 transition-transform duration-300 shrink-0 ${isOpen ? 'rotate-180' : ''}`} data-id="element-745" />
      </button>
      <AnimatePresence data-id="element-746">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-747">
            <ul className="px-6 pb-6 space-y-3" data-id="element-748">
              {module.lessons.map((lesson, i) => <li key={i} className="flex items-center gap-3 font-body text-warm-gray-700" data-id="element-749">
                  <Play className="w-4 h-4 text-accent-500 shrink-0" data-id="element-750" />
                  <span data-id="element-751">{lesson}</span>
                </li>)}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function VideoCoursePage() {
  return <div className="w-full bg-warm-white" data-id="element-752">
      {/* 1. HERO */}
      <div className="bg-primary-900 text-warm-white py-20 lg:py-28" data-id="element-753">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-754">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-800 text-accent-400 font-body text-sm font-semibold mb-6 border border-primary-700" data-id="element-755">
            <Monitor className="w-4 h-4" data-id="element-756" />
            <span data-id="element-757">Self-Paced Video Learning</span>
          </div>
          <h1 className="text-4xl lg:text-6xl font-heading mb-6" data-id="element-758">
            Complete Pet CPR & First Aid
            <br data-id="element-759" />
            <span className="text-accent-400" data-id="element-760">Video Course</span>
          </h1>
          <p className="text-xl text-primary-100 font-body max-w-2xl mx-auto mb-10" data-id="element-761">
            25+ expert-led video lessons covering every core pet emergency
            skill. Learn at your own pace, on any device, with lifetime access.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-762">
            <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 border border-transparent text-lg font-bold rounded-full text-primary-900 bg-accent-500 hover:bg-accent-400 shadow-warm-lg transition-all hover:-translate-y-1" data-id="element-763">
              Enroll Now — $89
            </Link>
            <a href="#curriculum" className="inline-flex justify-center items-center px-8 py-4 border-2 border-warm-white text-lg font-bold rounded-full text-warm-white bg-transparent hover:bg-white/10 transition-all" data-id="element-764">
              Preview Curriculum
            </a>
          </div>
        </div>
      </div>

      {/* 2. WHAT'S INCLUDED */}
      <AnimatedSection className="py-24 bg-warm-white" data-id="element-765">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-766">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-767">
            <h2 className="text-3xl lg:text-4xl font-heading text-primary-900 mb-4" data-id="element-768">
              What's Included
            </h2>
            <p className="text-warm-gray-600 font-body text-lg" data-id="element-769">
              The complete PetSaver-Lite curriculum plus detailed skill-topic
              video instruction — everything you need to respond confidently to
              a pet emergency.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-770">
            {[{
            icon: Play,
            title: '25+ Video Lessons',
            desc: 'HD demonstrations of every core technique, filmed with professional mannequins.'
          }, {
            icon: BookOpen,
            title: 'Full PetSaver-Lite Theory',
            desc: 'Complete foundational knowledge covering anatomy, assessment, and emergency protocols.'
          }, {
            icon: ClipboardCheck,
            title: 'Knowledge Checks',
            desc: 'Quizzes after each module to reinforce learning and confirm understanding.'
          }, {
            icon: Repeat,
            title: 'Lifetime Access',
            desc: 'Rewatch any lesson anytime. Your access never expires, and content is updated regularly.'
          }, {
            icon: Monitor,
            title: 'Any Device',
            desc: 'Watch on desktop, tablet, or phone. Pick up where you left off across devices.'
          }, {
            icon: Award,
            title: 'Certificate of Completion',
            desc: 'Receive a digital certificate after finishing all modules and passing knowledge checks.'
          }].map((item, i) => <div key={i} className="bg-white rounded-2xl p-8 shadow-warm border border-warm-gray-100" data-id="element-771">
                <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mb-6" data-id="element-772">
                  <item.icon className="w-6 h-6 text-primary-800" data-id="element-773" />
                </div>
                <h3 className="text-xl font-heading text-primary-900 mb-3" data-id="element-774">
                  {item.title}
                </h3>
                <p className="text-warm-gray-600 font-body leading-relaxed" data-id="element-775">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 3. CURRICULUM */}
      <AnimatedSection id="curriculum" className="py-24 bg-warm-gray-50" data-id="element-776">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-777">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-778">
            <h2 className="text-3xl lg:text-4xl font-heading text-primary-900 mb-4" data-id="element-779">
              Course Curriculum
            </h2>
            <p className="text-warm-gray-600 font-body text-lg" data-id="element-780">
              7 modules. 25+ lessons. Organized from fundamentals to advanced
              emergency response.
            </p>
          </div>

          <div className="space-y-4" data-id="element-781">
            {modules.map((module, i) => <ModuleItem key={i} module={module} index={i} data-id="element-782" />)}
          </div>

          <div className="mt-12 text-center" data-id="element-783">
            <p className="font-body text-warm-gray-500 text-sm" data-id="element-784">
              Total: {modules.reduce((acc, m) => acc + m.lessons.length, 0)}{' '}
              lessons across {modules.length} modules · ~6 hours of content
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* 4. LEARNING EXPERIENCE */}
      <AnimatedSection className="py-24 bg-warm-white" data-id="element-785">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-786">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-787">
            <h2 className="text-3xl lg:text-4xl font-heading text-primary-900 mb-4" data-id="element-788">
              How Learning Works
            </h2>
            <p className="text-warm-gray-600 font-body text-lg" data-id="element-789">
              Designed for busy schedules. Each lesson is focused, practical,
              and built to stick.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-790">
            {[{
            icon: Play,
            title: 'Short, Focused Videos',
            desc: 'Each lesson is 10–20 minutes. No filler — just clear, expert demonstration.'
          }, {
            icon: Zap,
            title: 'Learn by Watching',
            desc: 'Every technique is shown step-by-step on professional mannequins with narrated instruction.'
          }, {
            icon: Repeat,
            title: 'Rewatch Anytime',
            desc: 'Revisit any lesson as often as you need. Lifetime access means no pressure.'
          }, {
            icon: ClipboardCheck,
            title: 'Check Your Knowledge',
            desc: "Short quizzes after each module confirm you've absorbed the key concepts."
          }].map((item, i) => <div key={i} className="text-center" data-id="element-791">
                <div className="w-16 h-16 bg-primary-50 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-792">
                  <item.icon className="w-7 h-7 text-primary-800" data-id="element-793" />
                </div>
                <h3 className="text-lg font-heading text-primary-900 mb-3" data-id="element-794">
                  {item.title}
                </h3>
                <p className="text-warm-gray-600 font-body text-sm leading-relaxed" data-id="element-795">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 5. WHO THIS IS FOR */}
      <AnimatedSection className="py-24 bg-warm-gray-50" data-id="element-796">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-797">
          <div className="text-center mb-12" data-id="element-798">
            <h2 className="text-3xl lg:text-4xl font-heading text-primary-900 mb-4" data-id="element-799">
              This Course Is For You If…
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-800">
            {[{
            icon: Heart,
            title: 'Pet Owners',
            desc: 'You want to be prepared for emergencies at home with your dog or cat.'
          }, {
            icon: Users,
            title: 'Pet Professionals',
            desc: 'Sitters, walkers, groomers, and trainers who want to add a critical skill.'
          }, {
            icon: Shield,
            title: 'Animal Care Staff',
            desc: 'Rescue volunteers, shelter workers, and daycare teams who work with animals daily.'
          }].map((item, i) => <div key={i} className="bg-white rounded-2xl p-8 shadow-warm border border-warm-gray-100 text-center" data-id="element-801">
                <item.icon className="w-10 h-10 text-accent-500 mx-auto mb-4" data-id="element-802" />
                <h3 className="font-heading text-xl text-primary-900 mb-3" data-id="element-803">
                  {item.title}
                </h3>
                <p className="font-body text-warm-gray-600 text-sm" data-id="element-804">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 6. WHAT THIS COURSE DOES NOT INCLUDE */}
      <AnimatedSection className="py-24 bg-warm-white" data-id="element-805">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-806">
          <div className="bg-warm-gray-50 rounded-2xl p-8 lg:p-12 border border-warm-gray-200" data-id="element-807">
            <h2 className="text-2xl font-heading text-primary-900 mb-6" data-id="element-808">
              Good to Know
            </h2>
            <p className="font-body text-warm-gray-700 mb-6 leading-relaxed" data-id="element-809">
              This video course covers the complete knowledge curriculum. To set
              the right expectations:
            </p>
            <ul className="space-y-4 font-body text-warm-gray-700" data-id="element-810">
              <li className="flex items-start gap-3" data-id="element-811">
                <span className="text-warm-gray-400 font-bold mt-0.5" data-id="element-812">—</span>
                <span data-id="element-813">
                  <strong className="text-primary-900" data-id="element-814">
                    No hands-on practice included.
                  </strong>{' '}
                  Physical skills practice with mannequins requires an in-person
                  workshop.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-815">
                <span className="text-warm-gray-400 font-bold mt-0.5" data-id="element-816">—</span>
                <span data-id="element-817">
                  <strong className="text-primary-900" data-id="element-818">
                    Certificate of Completion only.
                  </strong>{' '}
                  The full 2-year professional certification requires an
                  additional in-person skills assessment.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </AnimatedSection>

      {/* 7. UPGRADE PATH */}
      <AnimatedSection className="py-24 bg-primary-50" data-id="element-819">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-820">
          <div className="text-center mb-12" data-id="element-821">
            <h2 className="text-2xl font-heading text-primary-900 mb-4" data-id="element-822">
              Want to Go Further?
            </h2>
            <p className="text-warm-gray-600 font-body" data-id="element-823">
              Your video course purchase applies toward these upgrades.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-824">
            <div className="bg-white rounded-2xl p-8 shadow-warm border border-warm-gray-100" data-id="element-825">
              <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center mb-4" data-id="element-826">
                <Users className="w-5 h-5 text-primary-800" data-id="element-827" />
              </div>
              <h3 className="font-heading text-xl text-primary-900 mb-2" data-id="element-828">
                Add a Workshop
              </h3>
              <p className="font-body text-warm-gray-600 text-sm mb-4 leading-relaxed" data-id="element-829">
                Get hands-on practice with professional mannequins and real-time
                instructor feedback at your location.
              </p>
              <Link to="/workshops" className="inline-flex items-center gap-2 text-primary-800 font-medium font-body hover:text-accent-600 transition-colors text-sm" data-id="element-830">
                Learn about workshops <ArrowRight className="w-4 h-4" data-id="element-831" />
              </Link>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-warm border border-warm-gray-100" data-id="element-832">
              <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center mb-4" data-id="element-833">
                <Award className="w-5 h-5 text-primary-800" data-id="element-834" />
              </div>
              <h3 className="font-heading text-xl text-primary-900 mb-2" data-id="element-835">
                Get Certified
              </h3>
              <p className="font-body text-warm-gray-600 text-sm mb-4 leading-relaxed" data-id="element-836">
                Complete the in-person skills assessment to earn your official
                2-year Pet CPR & First Aid Certification.
              </p>
              <Link to="/programs" className="inline-flex items-center gap-2 text-primary-800 font-medium font-body hover:text-accent-600 transition-colors text-sm" data-id="element-837">
                View certification details <ArrowRight className="w-4 h-4" data-id="element-838" />
              </Link>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 7.5. TRUST BOOST */}
      <VideoCourseTrust data-id="element-839" />

      {/* 8. PRICING / ENROLLMENT CTA */}
      <AnimatedSection className="py-24 bg-primary-800 relative overflow-hidden" data-id="element-840">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-841"></div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-842">
          <h2 className="text-4xl lg:text-5xl font-heading text-warm-white mb-6" data-id="element-843">
            Start Learning Today
          </h2>
          <div className="mb-8" data-id="element-844">
            <span className="text-5xl font-heading text-accent-400" data-id="element-845">$89</span>
            <span className="text-xl text-primary-100 font-body ml-3" data-id="element-846">
              one-time · lifetime access
            </span>
          </div>
          <ul className="inline-flex flex-col items-start gap-3 text-primary-100 font-body mb-10" data-id="element-847">
            <li className="flex items-center gap-3" data-id="element-848">
              <Check className="w-5 h-5 text-accent-400" data-id="element-849" />
              <span data-id="element-850">25+ expert-led video lessons</span>
            </li>
            <li className="flex items-center gap-3" data-id="element-851">
              <Check className="w-5 h-5 text-accent-400" data-id="element-852" />
              <span data-id="element-853">Full PetSaver-Lite curriculum</span>
            </li>
            <li className="flex items-center gap-3" data-id="element-854">
              <Check className="w-5 h-5 text-accent-400" data-id="element-855" />
              <span data-id="element-856">Knowledge checks & certificate</span>
            </li>
            <li className="flex items-center gap-3" data-id="element-857">
              <Check className="w-5 h-5 text-accent-400" data-id="element-858" />
              <span data-id="element-859">Watch on any device, forever</span>
            </li>
          </ul>
          <Link to="/contact" className="inline-flex justify-center items-center px-10 py-5 border border-transparent text-lg font-bold rounded-full text-primary-900 bg-accent-500 hover:bg-accent-400 shadow-warm-lg transition-all hover:-translate-y-1" data-id="element-860">
            Enroll Now — $89
          </Link>
        </div>
      </AnimatedSection>

      {/* 9. FAQ */}
      <AnimatedSection className="py-24 bg-warm-white" data-id="element-861">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-862">
          <div className="text-center mb-16" data-id="element-863">
            <h2 className="text-3xl lg:text-4xl font-heading text-primary-900 mb-4" data-id="element-864">
              Common Questions
            </h2>
          </div>

          <div className="bg-white rounded-2xl shadow-warm border border-warm-gray-100 p-6 md:p-8" data-id="element-865">
            {faqs.map((faq, i) => <FAQItem key={i} question={faq.q} answer={faq.a} data-id="element-866" />)}
          </div>
        </div>
      </AnimatedSection>
    </div>;
}